import { Locale, LOCALE_INFO } from 'base/ui/locales';
import { LLMProxyService } from 'blocks/llm_proxy_service';

export async function fitTextToNumLines(
  originalText: string,
  measureLineCount: (text: string) => number,
  targetLineCount: number,
  llm: LLMProxyService,
  locale: Locale,
  gotPartialResult?: (text: string) => void,
): Promise<{
  text: string;
  apology?: string;
}> {
  const originalLineCount = measureLineCount(originalText);
  if (originalLineCount === targetLineCount) {
    return { text: originalText };
  }
  const direction = targetLineCount > originalLineCount ? +1 : -1;
  // Breaks ties in the direction of the target
  const targetForScoring = targetLineCount + direction * 0.2;
  const originalWordCount = originalText.split(/\s+/).length;
  const originalWpl = originalWordCount / originalLineCount;

  let lastWpl = originalWpl;
  // Results by targetWordCount
  const results = new Map<
    number,
    { text: string; score: number; lineCount: number }
  >();

  for (let tryCount = 0; tryCount < 3; tryCount++) {
    let targetWordCount = Math.max(1, Math.floor(targetLineCount * lastWpl));
    while (results.has(targetWordCount)) {
      targetWordCount += direction;
    }

    let text = '';
    for await (const chunk of llm.infer('text-fit-1', {
      input: originalText,
      instruction: formatInstruction(originalWordCount, targetWordCount),
      language: LOCALE_INFO[locale].name,
    })) {
      text += chunk;
      gotPartialResult?.(text);
    }
    text = maybeStripQuotes(text, originalText);
    const lineCount = measureLineCount(text);
    const score = Math.abs(lineCount - targetForScoring);
    results.set(targetWordCount, { text, score, lineCount });
    lastWpl = targetWordCount / lineCount;
    if (lineCount === targetLineCount) {
      break;
    }
  }

  const { text, lineCount } = minBy(results.values(), (r) => r.score)!;
  return { text, apology: formatApology(lineCount, targetLineCount) };
}

function formatApology(actual: number, target: number) {
  if (actual === target) {
    return undefined;
  }
  const lines = (n: number) => (n === 1 ? '1 line' : `${n} lines`);
  return `We couldn't hit ${lines(target)} exactly.  Here's our best ${lines(
    actual,
  )} option.`;
}

/** If the original string is short, the target word count might become negative. */
const NEGATIVE_INSTRUCTIONS = new Map<number, string>([
  [0, ' Use a shorter word.'],
  [-1, ' Use a much shorter word.'],
  [-2, ' Choose a word with very few characters.'],
]);

function formatInstruction(original: number, target: number) {
  const targetSafe = Math.max(1, target);
  const trailer = NEGATIVE_INSTRUCTIONS.get(target) ?? '';
  const words = (n: number) => (n === 1 ? '1 word' : `${n} words`);
  return (
    `The user's text is ${words(
      original,
    )}. Rewrite it to be approximately ${words(targetSafe)}.` + trailer
  );
}

function minBy<T>(
  items: Iterable<T>,
  score: (item: T) => number,
): T | undefined {
  let bestItem: T | undefined;
  let bestScore: number | undefined;
  for (const item of items) {
    const itemScore = score(item);
    if (bestScore == null || itemScore < bestScore) {
      bestScore = itemScore;
      bestItem = item;
    }
  }
  return bestItem;
}

function maybeStripQuotes(result: string, reference: string) {
  // If the reference had quotes, don't remove them from the result.
  if (reference.startsWith('"') && reference.endsWith('"')) {
    return result;
  }
  return result.replace(/^"|"$/g, '');
}
